/**
 * Statement PDF Generator & Uploader
 *
 * Generates a customer statement PDF client-side using the existing InvoiceTemplate
 * and uploads it to Supabase temporary storage. The Edge Function then downloads
 * this file and attaches it to the Xero invoice before deleting the temp file.
 *
 * Flow:
 *   1. Fetch invoice balances + payments for customer
 *   2. Build mini-statement entries via InvoicePDFGenerator.generateMiniStatement
 *   3. Generate PDF blob via InvoicePDFGenerator.generatePDF
 *   4. Upload to `invoices/statements/temp/{uuid}.pdf`
 *   5. Return the storage path for the Edge Function to consume
 */

import { supabase } from '@/supabase-client'
import InvoicePDFGenerator from '@/components/invoice/InvoicePDFGenerator'
import { toLocalDateString } from './dateUtils'

/**
 * Generate a statement PDF and upload it to temp Supabase storage.
 *
 * @param {string} customerId - UUID of the customer
 * @param {'since_zero' | 'transactions'} statementType - Statement scope
 * @param {object} options
 * @param {object} options.farmInfo - Farm/business info for the PDF header (required)
 * @param {object} options.customerInfo - Customer info ({ name, ... }) for the PDF header
 * @returns {Promise<string>} The storage path (relative to the `invoices` bucket)
 */
export async function generateAndUploadStatement(customerId, statementType, { farmInfo, customerInfo } = {}) {
  if (!customerId) throw new Error('customerId is required')
  if (!farmInfo) throw new Error('farmInfo is required to generate statement PDF')

  // ------------------------------------------------------------------
  // 1. Fetch invoice balances for the customer
  // ------------------------------------------------------------------
  const { data: invoices, error: invError } = await supabase
    .from('invoice_balances')
    .select('invoice_id, invoice_no, date, invoice_total, amount_paid, amount_credited, amount_due, calculated_status, is_legacy_paid')
    .eq('customer_id', customerId)
    .order('date', { ascending: true })

  if (invError) throw new Error(`Failed to fetch invoice balances: ${invError.message}`)

  // ------------------------------------------------------------------
  // 2. Fetch payments for the customer
  // ------------------------------------------------------------------
  const { data: payments, error: payError } = await supabase
    .from('payments')
    .select('id, date, amount, method')
    .eq('customer_id', customerId)
    .order('date', { ascending: true })

  if (payError) throw new Error(`Failed to fetch payments: ${payError.message}`)

  // ------------------------------------------------------------------
  // 3. Build mini-statement entries using InvoicePDFGenerator
  // ------------------------------------------------------------------
  const invoiceEntries = (invoices || []).map((inv) => ({
    date: inv.date,
    invoice_no: inv.invoice_no,
    total: inv.invoice_total,
    paid: inv.calculated_status === 'paid'
  }))

  const paymentEntries = (payments || []).map((p) => ({
    date: p.date,
    amount: p.amount,
    method: p.method
  }))

  const miniStatement = InvoicePDFGenerator.generateMiniStatement(
    invoiceEntries,
    paymentEntries,
    { type: statementType, transactionCount: 10 }
  )

  // ------------------------------------------------------------------
  // 4. Build minimal invoice data structure required by InvoiceTemplate
  //    (we only need the customer name; line items are empty)
  // ------------------------------------------------------------------
  const invoiceData = {
    invoiceNumber: 'STATEMENT',
    date: toLocalDateString(new Date()),
    customer: customerInfo || { name: 'Customer' },
    lineItems: [],
    total: 0,
    subtotal: 0,
    vat: 0
  }

  // ------------------------------------------------------------------
  // 5. Generate PDF blob
  // ------------------------------------------------------------------
  const blob = await InvoicePDFGenerator.generatePDF(invoiceData, farmInfo, {
    showMiniStatement: true,
    miniStatement
  })

  // ------------------------------------------------------------------
  // 6. Upload to Supabase Storage (temp path, cleaned up by Edge Function)
  // ------------------------------------------------------------------
  const uuid = crypto.randomUUID()
  const storagePath = `statements/temp/${uuid}.pdf`

  const { error: uploadError } = await supabase
    .storage
    .from('invoices')
    .upload(storagePath, blob, {
      contentType: 'application/pdf',
      upsert: false
    })

  if (uploadError) throw new Error(`Failed to upload statement to storage: ${uploadError.message}`)

  return storagePath
}
